import { useState, useEffect, useRef } from "react";
import { Play, Pause, RotateCcw } from "lucide-react";

// 20-minute urge-surfing timer with breathing guide and phase feedback
// Based on Marlatt & Gordon (1985) urge surfing model

const TOTAL_MS = 20 * 60 * 1000;
const R        = 58;
const CIRC     = 2 * Math.PI * R;

const PHASES = [
  { until: 5  * 60000, color: "#F4A261", bg: "#FFF7F0", label: "La ola sube…",      sub: "Normal sentirlo. Obsérvalo sin actuar." },
  { until: 10 * 60000, color: "#E07A5F", bg: "#FFF2EE", label: "En la cima",         sub: "La intensidad máxima. Solo dura un instante." },
  { until: 15 * 60000, color: "#F4C261", bg: "#FFFBEE", label: "Comienza a bajar",   sub: "Ya pasó lo peor. La ola se debilita." },
  { until: 20 * 60000, color: "#6B9080", bg: "#EEF4F1", label: "La ola se disipa",   sub: "Lo lograste. El impulso no tomó el control." },
];

// breath: 4s inhala · 4s mantén · 6s exhala = 14s cycle
const CYCLE_MS = 14000;

export default function UrgeSurf() {
  const [ms, setMs]           = useState(0);
  const [running, setRunning] = useState(false);
  const [done, setDone]       = useState(false);
  const rafRef  = useRef(null);
  const startTs = useRef(null);
  const baseMs  = useRef(0);

  useEffect(() => {
    if (!running) { cancelAnimationFrame(rafRef.current); return; }
    startTs.current = performance.now();
    const tick = (now) => {
      const elapsed = baseMs.current + (now - startTs.current);
      if (elapsed >= TOTAL_MS) {
        setMs(TOTAL_MS); setRunning(false); setDone(true); return;
      }
      setMs(elapsed);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [running]);

  const toggle = () => {
    if (done) return;
    if (running) { baseMs.current = ms; }
    setRunning(r => !r);
  };

  const reset = () => {
    cancelAnimationFrame(rafRef.current);
    baseMs.current = 0;
    setMs(0); setRunning(false); setDone(false);
  };

  // Derived
  const phase    = PHASES.find(p => ms < p.until) ?? PHASES[PHASES.length - 1];
  const progress = ms / TOTAL_MS;
  const dashOff  = CIRC * (1 - progress);
  const secs     = Math.floor(ms / 1000);
  const mm       = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss       = String(secs % 60).padStart(2, "0");

  // Breathing
  const pos = ms % CYCLE_MS;
  let breathLabel, breathColor, breathScale;
  if (pos < 4000) {
    breathLabel = "Inhala"; breathColor = "#5B8FCC";
    breathScale = 0.72 + (pos / 4000) * 0.58;
  } else if (pos < 8000) {
    breathLabel = "Mantén"; breathColor = "#8B6BAE";
    breathScale = 1.3;
  } else {
    breathLabel = "Exhala"; breathColor = "#6B9080";
    breathScale = 1.3 - ((pos - 8000) / 6000) * 0.58;
  }
  if (!running) breathScale = 1;

  return (
    <div className="bg-white rounded-2xl border border-[#E4EBE6] overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-3">
        <p className="text-sm font-bold text-[#1E2F23]" style={{ fontFamily: "Manrope" }}>
          Surfea el impulso
        </p>
        <p className="text-xs text-[#2C4238] mt-0.5">
          Los cravings duran ~20 min. Obsérvalo como una ola — no tienes que actuar.
        </p>
      </div>

      <div className="px-5 pb-6 flex flex-col items-center gap-5">

        {/* Circular progress ring */}
        <div className="relative" style={{ width: 152, height: 152 }}>
          <svg width="152" height="152" style={{ transform: "rotate(-90deg)" }}>
            <circle cx="76" cy="76" r={R} fill="none" stroke="#F0F5F2" strokeWidth="7" />
            <circle
              cx="76" cy="76" r={R}
              fill="none"
              stroke={done ? "#6B9080" : phase.color}
              strokeWidth="7"
              strokeLinecap="round"
              strokeDasharray={CIRC}
              strokeDashoffset={dashOff}
              style={{ transition: "stroke-dashoffset 0.1s linear, stroke 1.5s" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {done
              ? <p className="text-4xl">💪</p>
              : <>
                  <p className="text-2xl font-bold text-[#1E2F23]" style={{ fontFamily: "Manrope" }}>{mm}:{ss}</p>
                  <p className="text-xs text-[#4A6E5C]">/ 20:00</p>
                </>
            }
          </div>
        </div>

        {/* Phase label */}
        <div
          className="rounded-2xl px-4 py-3 w-full text-center"
          style={{ backgroundColor: done ? "#EEF4F1" : phase.bg }}
        >
          <p className="text-sm font-bold mb-0.5" style={{ color: done ? "#6B9080" : phase.color, fontFamily: "Manrope" }}>
            {done ? "Lo lograste 💪" : phase.label}
          </p>
          <p className="text-xs text-[#2C4238]">
            {done ? "El impulso pasó. Tu corteza prefrontal ganó." : phase.sub}
          </p>
        </div>

        {/* Breathing indicator */}
        {!done && (
          <div className="flex flex-col items-center gap-2">
            <div
              className="rounded-full border-2 flex items-center justify-center"
              style={{
                width: 56, height: 56,
                borderColor: running ? breathColor : "#E4EBE6",
                backgroundColor: running ? breathColor + "25" : "#F7F9F7",
                transform: `scale(${breathScale})`,
                transition: "transform 0.25s ease-in-out, border-color 0.4s, background-color 0.4s",
              }}
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: running ? breathColor : "#D1D9D5", transition: "background-color 0.4s" }}
              />
            </div>
            <p className="text-xs font-semibold text-[#2C4238]">
              {running ? breathLabel : "respiración guiada"}
            </p>
          </div>
        )}

        {/* Controls */}
        <div className="flex gap-3 w-full">
          {!done && (
            <button
              onClick={toggle}
              className="flex-1 py-3 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition-all active:scale-95"
              style={running
                ? { backgroundColor: "#F7F9F7", color: "#526B5D", border: "1px solid #E4EBE6" }
                : { backgroundColor: phase.color, color: "white" }
              }
            >
              {running
                ? <><Pause size={15} /> Pausar</>
                : <><Play  size={15} /> {ms === 0 ? "Iniciar" : "Continuar"}</>
              }
            </button>
          )}
          <button
            onClick={reset}
            className="w-12 h-12 rounded-2xl bg-[#F7F9F7] border border-[#E4EBE6] flex items-center justify-center active:scale-95 transition-transform flex-shrink-0"
          >
            <RotateCcw size={15} className="text-[#2C4238]" />
          </button>
        </div>

        <p className="text-xs text-[#4A6E5C] text-center leading-relaxed">
          Técnica de urge surfing · Marlatt &amp; Gordon (1985) · Evidencia en reducción de consumo
        </p>
      </div>
    </div>
  );
}
